import FluxContainerSubscriptions from "flux/lib/FluxContainerSubscriptions";
import distinctUntilChangedBuilder from "data/util/distinctUntilChangedBuilder";

const DEFAULT_OPTIONS = {
    async: true,
};

/*
 * I am basically identical to Flux's Container's `createFunctional`, except
 * instead of taking a React render function as my first parameter, I take an
 * arbitrary void callback. Like the render function, the callback will be
 * passed the result of invoking calculateState, though there is no concept of
 * the props or context passthrough Container provides. That is, the callback
 * and calculateState will always be passed a single arg, and getStores will be
 * passed zero args.
 *
 * Where Container returns a React HoC, CallbackContainer returns a token that
 * can be used to close it. Calling the `close()` function on the returned token
 * is equivalent to unmounting the React HoC that Container creates. Similarly,
 * where React doesn't rerender on no-op state changes, the callback won't be
 * reinvoked on no-op state changes.
 *
 * By default, the callback will be executed _asynchronously_. You may pass
 * `{async:false}` as a fourth parameter to override this behaviour.
 *
 * The use of this helper should be consider a code smell, and only invoked to
 * deal with the Flux vs. Backbone impedance mismatch. Callbacks created this
 * way are trivially converted to a Store's reduce function by creating a `case`
 * that `.waitFor`s the result of getStores, runs `calculateState` to get data
 * from them, and if it's changed, does the callback to update the store.
 */
const createFunctional = (
    callback,
    getStores,
    calculateState,
    options,
) => {
    let isOpen = true;
    const realOptions = {
        ...DEFAULT_OPTIONS,
        ...(options || {}),
    };

    let prevState = undefined;
    const _fluxContainerSubscriptions = new FluxContainerSubscriptions();
    _fluxContainerSubscriptions.setStores(getStores());
    _fluxContainerSubscriptions.addListener(
        distinctUntilChangedBuilder(
            () => calculateState(prevState),
            state => {
                prevState = state;
                if (realOptions.async) {
                    // This is specifically designed to allow callbacks into Backbone
                    // stuff, which may go back into Flux stuff and create a reentrant
                    // dispatch.
                    //
                    // eslint-disable-next-line no-settimeout
                    setTimeout(() => {
                        if (isOpen) {
                            callback(state);
                        }
                    });
                } else {
                    callback(state);
                }
            },
        ));

    return {
        force() {
            callback(calculateState(prevState));
        },
        close() {
            _fluxContainerSubscriptions.reset();
            isOpen = false;
        },
    };
};

export default { createFunctional };



// WEBPACK FOOTER //
// ./app/data/util/CallbackContainer.js